import { useMemo, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Points, PointMaterial, Line, Stars } from "@react-three/drei";
import * as THREE from "three";
import StaticBackdrop from "./StaticBackdrop";

const BLUE = "#00c8ff";
const PURPLE = "#6c5ce7";
const R = 2.15;

/* Fibonacci sphere distribution */
function fibSphere(count, radius) {
  const pts = [];
  const phi = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < count; i++) {
    const y = 1 - (i / (count - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const theta = phi * i;
    pts.push(new THREE.Vector3(Math.cos(theta) * r, y, Math.sin(theta) * r).multiplyScalar(radius));
  }
  return pts;
}

function NodeGlobe() {
  const group = useRef();
  const nodes = useMemo(() => fibSphere(520, R), []);

  const positions = useMemo(() => {
    const arr = new Float32Array(nodes.length * 3);
    nodes.forEach((v, i) => {
      arr[i * 3] = v.x;
      arr[i * 3 + 1] = v.y;
      arr[i * 3 + 2] = v.z;
    });
    return arr;
  }, [nodes]);

  const arcs = useMemo(() => {
    const list = [];
    for (let i = 0; i < 26; i++) {
      const a = nodes[Math.floor(Math.random() * nodes.length)];
      const b = nodes[Math.floor(Math.random() * nodes.length)];
      if (a.distanceTo(b) < 1.5) continue;
      const mid = a.clone().add(b).multiplyScalar(0.5).normalize().multiplyScalar(R * (1.25 + Math.random() * 0.35));
      const curve = new THREE.QuadraticBezierCurve3(a, mid, b);
      list.push({
        pts: curve.getPoints(40).map((p) => [p.x, p.y, p.z]),
        color: Math.random() > 0.5 ? BLUE : PURPLE,
        speed: 0.4 + Math.random() * 0.6,
      });
    }
    return list;
  }, [nodes]);

  useFrame((state, delta) => {
    if (group.current) group.current.rotation.y += delta * 0.06;
  });

  return (
    <group ref={group}>
      {/* inner glow sphere */}
      <mesh>
        <sphereGeometry args={[R * 0.99, 48, 48]} />
        <meshBasicMaterial color="#03202b" transparent opacity={0.35} />
      </mesh>
      {/* wireframe shell */}
      <mesh>
        <icosahedronGeometry args={[R * 1.005, 3]} />
        <meshBasicMaterial color={BLUE} wireframe transparent opacity={0.08} />
      </mesh>
      {/* nodes */}
      <Points positions={positions} stride={3}>
        <PointMaterial transparent color={BLUE} size={0.045} sizeAttenuation depthWrite={false} opacity={0.9} />
      </Points>
      {/* arcs */}
      {arcs.map((a, i) => (
        <Line key={i} points={a.pts} color={a.color} lineWidth={1} transparent opacity={0.55} />
      ))}
    </group>
  );
}

function FloatingParticles() {
  const ref = useRef();
  const positions = useMemo(() => {
    const arr = new Float32Array(900 * 3);
    for (let i = 0; i < 900; i++) {
      arr[i * 3] = (Math.random() - 0.5) * 22;
      arr[i * 3 + 1] = (Math.random() - 0.5) * 14;
      arr[i * 3 + 2] = (Math.random() - 0.5) * 14;
    }
    return arr;
  }, []);

  useFrame((state, delta) => {
    if (ref.current) {
      ref.current.rotation.y += delta * 0.01;
      ref.current.rotation.x += delta * 0.004;
    }
  });

  return (
    <Points ref={ref} positions={positions} stride={3}>
      <PointMaterial transparent color="#88d8ff" size={0.02} sizeAttenuation depthWrite={false} opacity={0.5} />
    </Points>
  );
}

function Rig({ globeRef }) {
  const { camera, pointer } = useThree();
  useFrame(() => {
    camera.position.x += (pointer.x * 0.9 - camera.position.x) * 0.04;
    camera.position.y += (pointer.y * 0.6 - camera.position.y) * 0.04;
    camera.lookAt(0, 0, 0);
    if (globeRef.current) {
      globeRef.current.rotation.x += (pointer.y * 0.25 - globeRef.current.rotation.x) * 0.04;
    }
  });
  return null;
}

export default function SpaceScene() {
  const tiltRef = useRef();
  // Decide once, synchronously, to avoid mounting the heavy Canvas then swapping.
  const [mode] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return "static";
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const small = window.matchMedia("(max-width: 820px)").matches;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const fewCores = (navigator.hardwareConcurrency || 8) <= 4;
    const lowMem = navigator.deviceMemory && navigator.deviceMemory <= 4;
    return reduce || small || (coarse && fewCores) || lowMem ? "static" : "full";
  });

  if (mode === "static") return <StaticBackdrop />;

  return (
    <Canvas
      camera={{ position: [0, 0, 6.2], fov: 45 }}
      dpr={[1, 1.8]}
      gl={{ antialias: true, alpha: true }}
      style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }}
    >
      <ambientLight intensity={0.6} />
      <pointLight position={[5, 5, 5]} intensity={1.2} color={BLUE} />
      <pointLight position={[-5, -3, 2]} intensity={0.8} color={PURPLE} />
      <Stars radius={80} depth={40} count={2600} factor={4} saturation={0} fade speed={0.6} />
      <FloatingParticles />
      <group ref={tiltRef}>
        <NodeGlobe />
      </group>
      <Rig globeRef={tiltRef} />
    </Canvas>
  );
}
